import { ICopyable } from '../core/copyable.interface';
import { Rights } from './enums/rights';
import { IAuthenticate } from './interface/authenticate.interface';
import { IUserParams } from './interface/user-params.interface';
import { Organization } from './organization';
import { ProfileSettings } from './profile-settings';
import { ServiceSubscription } from './service-subscription';
import { Token } from './token';

export class Authenticate implements ICopyable<Authenticate> {
    public userId: string = null;
    public login: string = null;
    public displayName: string = null;
    public organization: Organization = null;
    public groupName: string = null;
    public roleName: string = null;
    public rights: Rights[] = null;
    public token: Token = null;
    public uiTheme: string = 'dark';
    public params: IUserParams;
    public serviceSubscriptions: ServiceSubscription[];
    public profileSettings: ProfileSettings;

    public createCopy(): Authenticate {
        const instance = new Authenticate();
        instance.userId = this.userId;
        instance.login = this.login;
        instance.displayName = this.displayName;
        instance.organization = this.organization;
        instance.groupName = this.groupName;
        instance.roleName = this.roleName;
        instance.rights = this.rights;
        instance.token = this.token;
        instance.uiTheme = this.uiTheme;
        instance.params = this.params;
        instance.serviceSubscriptions = this.serviceSubscriptions;
        instance.profileSettings = ProfileSettings.from(this.profileSettings);
        return instance;
    }

    public copyFrom(other: any) {
        this.userId = other.userId;
        this.login = other.login;
        this.displayName = other.displayName;
        this.organization = other.organization;
        this.groupName = other.groupName;
        this.roleName = other.roleName;
        this.rights = other.rights;
        this.token = other.token;
        this.uiTheme = other.uiTheme;
        this.params = other.params;
        this.serviceSubscriptions = other.serviceSubscriptions;
        this.profileSettings = ProfileSettings.from(other.profileSettings);
    }

    /**
     * Retourne une instance de Authenticate à partir d'une instance de IAuthenticate.
     * @param other : Instance de IAuthenticate.
     * @returns : Instance de Authenticate.
     */
    public static from(other: IAuthenticate) {
        if (!!other) {
            const instance = new Authenticate();

            instance.userId = other.userId;
            instance.login = other.login;
            instance.displayName = other.displayName;
            instance.organization = Organization.from(other.organization);
            instance.groupName = other.groupName;
            instance.roleName = other.roleName;
            instance.rights = other.rights;
            instance.serviceSubscriptions = other.serviceSubscriptions;
            let token = new Token();
            token.accessToken = other.token.accessToken;
            token.expireOn = new Date(other.token.expireOn);
            token.passwordExpireOn = new Date(other.token.passwordExpireOn);
            instance.token = token;
            instance.uiTheme = other.uiTheme;
            instance.params = other.params;
            instance.profileSettings = ProfileSettings.from(other.profileSettings);

            return instance;
        } else {
            return null;
        }
    }

    /**
     * Indique si l'utilisateur possède le droit spécifié.
     * @param right : Le droit a tester.
     * @returns True, si l'utilisateur possède le droit spécifié.
     */
    public hasRight(right: Rights): boolean {
        if (!!this.rights) {
            return this.rights.some((x) => x == right);
        } else {
            return false;
        }
    }

    /**
     * Indique si l'utilisateur possède l'un des droits spécifiés.
     * @param expectedRights : Les droits a tester.
     * @returns True, si l'utilisateur possède l'un des droits spécifiés.
     */
    public hasSomeRight(...expectedRights: Rights[]): boolean {
        if (!!this.rights) {
            return expectedRights.some((x) => this.rights.includes(x));
        } else {
            return false;
        }
    }
}
